/*
 * VideoStudio.js — Video Generator natywny dla MASKIL (T77 2026-06-04).
 * Inspirowany modułem Manusa, ale podpięty do DANYCH MASKILa: wybierasz przetarg/
 * inwestycję → AI układa brief sprzedażowy + clip plan → produkcyjny blueprint markdown.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Clapperboard, Sparkles, Download, Loader2, Clock, FileText } from 'lucide-react';
import { createVideoBlueprint, listVideoBlueprints, listTenders } from '../lib/api';

export default function VideoStudio() {
  const [oppId, setOppId] = useState('');
  const [oppOptions, setOppOptions] = useState([]);
  const [useAi, setUseAi] = useState(true);
  const [title, setTitle] = useState('');
  const [aspect, setAspect] = useState('16:9');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try { const r = await listVideoBlueprints(20); setHistory(r.items || []); } catch { /* noop */ }
  };
  const loadOpps = async () => {
    try {
      const r = await listTenders({ page: 1, page_size: 40, sort: 'match' });
      setOppOptions((r.items || []).map(t => ({
        id: t.source_id, label: `[${t.source}] ${(t.title || '').slice(0, 70)}`,
      })));
    } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); loadOpps(); }, []);

  const generate = async () => {
    if (!oppId && !title.trim()) {
      toast.warning('Wybierz przetarg/inwestycję albo wpisz tytuł wideo.');
      return;
    }
    setGenerating(true);
    const tId = toast.loading(useAi && oppId ? 'AI układa brief z danych okazji...' : 'Generuję blueprint...');
    try {
      const body = { aspect_ratio: aspect, use_ai: useAi };
      if (oppId) body.opp_id = oppId;
      if (title.trim()) body.title = title.trim();
      const r = await createVideoBlueprint(body);
      setResult(r);
      toast.success(`Blueprint gotowy (${r.total_duration}s, źródło: ${r.source}).`, { id: tId });
      loadHistory();
    } catch (e) {
      toast.error('Nie udało się: ' + (e.response?.data?.detail || e.message), { id: tId });
    } finally {
      setGenerating(false);
    }
  };

  const downloadMd = () => {
    if (!result?.markdown) return;
    const blob = new Blob([result.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(result.brief?.title || 'blueprint').replace(/[^a-z0-9]+/gi, '_').slice(0, 60)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clapperboard className="w-6 h-6 text-primary" />
            Video Studio
          </h1>
          <p className="text-sm text-muted-foreground">
            Brief produkcyjny wideo sprzedażowego — z konkretnego przetargu/inwestycji, układany przez AI MASKIL.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Konfiguracja */}
        <div className="col-span-4 space-y-4">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-base">Brief</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Przetarg / inwestycja (źródło danych)</label>
                <Select value={oppId} onValueChange={setOppId}>
                  <SelectTrigger><SelectValue placeholder="— wybierz okazję —" /></SelectTrigger>
                  <SelectContent>
                    {oppOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">…albo własny tytuł</label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="np. MASKIL — pitch dla samorządów" />
              </div>
              <div className="flex gap-3 items-center">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground block mb-1">Format</label>
                  <Select value={aspect} onValueChange={setAspect}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16:9">16:9 (poziom)</SelectItem>
                      <SelectItem value="9:16">9:16 (pion / reels)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-xs mt-5 cursor-pointer">
                  <input type="checkbox" checked={useAi} onChange={e => setUseAi(e.target.checked)} />
                  Brief od AI
                </label>
              </div>
              <Button onClick={generate} disabled={generating} className="w-full gap-2">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? 'Generuję...' : 'Generuj blueprint'}
              </Button>
            </CardContent>
          </Card>

          {/* Historia */}
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Historia</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {history.length === 0 && <p className="text-xs text-muted-foreground">Brak — wygeneruj pierwszy.</p>}
              {history.map(h => (
                <div key={h.id} className="text-xs p-2 rounded hover:bg-muted/40">
                  <div className="font-medium truncate">{h.title || '(bez tytułu)'}</div>
                  <div className="text-muted-foreground">{h.total_duration}s · {new Date(h.created_at).toLocaleString('pl-PL')}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Podgląd blueprintu */}
        <div className="col-span-8">
          <Card className="glass-card h-full">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Blueprint</CardTitle>
              {result?.markdown && (
                <Button size="sm" variant="outline" onClick={downloadMd} className="gap-1.5">
                  <Download className="w-4 h-4" /> Pobierz .md
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {result?.warnings?.length > 0 && (
                <div className="mb-3 text-xs text-amber-300 space-y-0.5">
                  {result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              {result?.markdown ? (
                <pre className="text-[11px] whitespace-pre-wrap leading-relaxed font-mono bg-black/20 rounded-lg p-4 max-h-[70vh] overflow-auto">
                  {result.markdown}
                </pre>
              ) : (
                <div className="text-center text-muted-foreground py-16">
                  <Clapperboard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Wybierz przetarg/inwestycję i kliknij „Generuj blueprint".</p>
                  <p className="text-xs mt-1">AI ułoży brief + clip plan z realnych danych okazji.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
